import React, { useMemo, useState } from 'react'
import SaberCard from '../components/SaberCard'
import SaberTable from '../components/SaberTable'
import StatusBadge from '../components/StatusBadge'
import type { SaberTableColumn } from '../components/SaberTable'
import { MultiSelectControl } from '../components/Controls'
import { useGetAlertsQuery, useGetFreshnessQuery } from '../saberApi'
import type { AlertRow, FreshnessRow } from '../saber.types'
import { ALL_PROPERTIES, useSaberSelection } from '../SaberContext'
import { date, dateTime, integer } from '../format'
import { CELL_COLOR } from '../cellColor'
import NullDash from '../components/NullDash'
import type { PanelStickyPropsFn } from '../stickyPanel'

// Order severities by rank for the filter dropdown; unknown values sort last.
const SEVERITY_ORDER = ['HIGH', 'MEDIUM', 'LOW']
const severityRank = (s: string): number => {
  const i = SEVERITY_ORDER.indexOf(s)
  return i === -1 ? SEVERITY_ORDER.length : i
}

export interface HealthProps {
  /**
   * v001 Config tab: when true, render ONLY the freshness + alerts tables with
   * no page chrome so the content nests inside a CollapsibleCard.
   */
  embedded?: boolean
  panelStickyProps?: PanelStickyPropsFn
  stickyZIndexStart?: number
}

// v001: the Latest Pipeline Runs card is gone — superseded by the Engine Status
// run history on the Config tab.
export const Health: React.FC<HealthProps> = ({
  embedded = false,
  panelStickyProps = () => ({}),
  stickyZIndexStart = 1
}) => {
  // null = untouched (= all severities). Multi-select; options come from the data.
  const [selectedSeverities, setSelectedSeverities] = useState<string[] | null>(null)

  // #7 — freshness honours the shared property selection (portfolio-wide when ALL).
  const { propertyCuid } = useSaberSelection()
  const freshnessArg = propertyCuid !== ALL_PROPERTIES ? { propertyCuid } : undefined

  const { data: freshness, isLoading: freshLoading, isError: freshError } = useGetFreshnessQuery(freshnessArg)
  const { data: alerts, isLoading: alertsLoading, isError: alertsError } = useGetAlertsQuery()

  // Severity filter options = the severities actually present in the alerts;
  // filtering is client-side so the option list stays stable as you filter.
  const allAlerts = useMemo(() => alerts ?? [], [alerts])
  const severityOptions = useMemo(
    () => [...new Set(allAlerts.map((a) => a.severity))].sort((a, b) => severityRank(a) - severityRank(b)),
    [allAlerts]
  )
  const activeSeverities = selectedSeverities ?? severityOptions
  const visibleAlerts = useMemo(
    () => allAlerts.filter((a) => activeSeverities.includes(a.severity)),
    [allAlerts, activeSeverities]
  )

  const freshnessColumns: Array<SaberTableColumn<FreshnessRow>> = useMemo(
    () => [
      { key: 'property', header: 'Property', sortable: true, sortValue: (r) => r.propertyName, render: (r) => <span className={CELL_COLOR.primary}>{r.propertyName}</span> },
      { key: 'source', header: 'Source Type', sortable: true, sortValue: (r) => r.sourceType, render: (r) => <StatusBadge status={r.sourceType} /> },
      { key: 'maxDate', header: 'Max Data Date', sortable: true, sortValue: (r) => r.maxDataDate, render: (r) => date(r.maxDataDate) },
      { key: 'age', header: 'Age (days)', align: 'right', sortable: true, sortValue: (r) => r.ageDays, render: (r) => (r.ageDays == null ? <NullDash /> : <span className={CELL_COLOR.muted}>{integer(r.ageDays)}</span>) },
      { key: 'status', header: 'Status', align: 'center', sortable: true, sortValue: (r) => r.status, render: (r) => <StatusBadge status={r.status} /> }
    ],
    []
  )

  const alertColumns: Array<SaberTableColumn<AlertRow>> = useMemo(
    () => [
      { key: 'severity', header: 'Severity', align: 'center', sortable: true, sortValue: (r) => r.severity, render: (r) => <StatusBadge status={r.severity} /> },
      { key: 'category', header: 'Category', sortable: true, sortValue: (r) => r.category, render: (r) => <StatusBadge status={r.category} /> },
      { key: 'title', header: 'Title', sortable: true, sortValue: (r) => r.title, render: (r) => <span className={CELL_COLOR.primary}>{r.title}</span> },
      { key: 'created', header: 'Created', sortable: true, sortValue: (r) => r.createdAt, render: (r) => dateTime(r.createdAt) },
      {
        key: 'ack',
        header: 'Acknowledged',
        align: 'center',
        render: (r) =>
          r.acknowledged ? (
            <StatusBadge status="OK" />
          ) : (
            <span className={CELL_COLOR.null}>No</span>
          )
      }
    ],
    []
  )

  const content = (
    <>
      <SaberCard title="Data-Source Freshness" {...panelStickyProps(stickyZIndexStart)}>
        {freshError ? (
          <p className="py-6 text-center text-sm text-red-600 dark:text-red-400">Failed to load freshness.</p>
        ) : freshLoading ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : (
          <SaberTable
            columns={freshnessColumns}
            rows={freshness ?? []}
            rowKey={(r) => `${r.propertyCuid}::${r.sourceType}`}
            emptyMessage="No freshness data."
            helpKey="freshness"
          />
        )}
      </SaberCard>

      <SaberCard
        title="Alerts"
        {...panelStickyProps(stickyZIndexStart + 1)}
        actions={
          <MultiSelectControl
            id="alerts-severity"
            label="Severity"
            options={severityOptions}
            selected={activeSeverities}
            onChange={setSelectedSeverities}
          />
        }
      >
        {alertsError ? (
          <p className="py-6 text-center text-sm text-red-600 dark:text-red-400">Failed to load alerts.</p>
        ) : alertsLoading ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : (
          <SaberTable
            columns={alertColumns}
            rows={visibleAlerts}
            rowKey={(r) => r.alertCuid}
            emptyMessage="No alerts."
            helpKey="alerts"
          />
        )}
      </SaberCard>
    </>
  )

  if (embedded) {
    return <div className="space-y-4">{content}</div>
  }

  return (
    <div className="space-y-4 py-4">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Health</h2>
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400">Data-source freshness and alerts.</p>
      </div>
      {content}
    </div>
  )
}

export default Health
